import {
  Table,
  TableBody,
  TableContainer,
  Typography,
  useTheme,
} from "@crayon/design-system-react";
import {
  GetProductSyncResultsResponse,
  ProductSyncResult,
  SyncOutcome,
} from "api/client.generated";
import TableBodyCell from "components/primitives/TableBodyCell";
import TableBodySkeleton from "components/primitives/TableBodySkeleton";
import TableBodySyncOutcomeCell from "components/primitives/TableBodySyncOutcomeCell";
import TableHeadSortable, { SortableColumn } from "components/primitives/TableHeadSortable";
import TableRowCollapsible from "components/primitives/TableRowCollapsible";
import { useMemo, useState } from "react";
import SortDirection from "types/sort-direction";
import compareModelsFn from "utils/sort-fn";

interface ProductSyncResultTableModel {
  sourceProductId: string;
  sourceProductName: string;
  sourceProductSku: string;
  targetProductId: string;
  targetProductName: string;
  costUnitPrice: number;
  retailUnitPrice: number;
  outcome: SyncOutcome;
  errorMessage?: string;
}

interface ProductSyncDetailsTableProps {
  productDigest: GetProductSyncResultsResponse | undefined;
  isProductDigestFetching: boolean;
}

const ProductSyncDetailsTable = (props: ProductSyncDetailsTableProps) => {
  const { productDigest, isProductDigestFetching } = props;
  const [sortBy, setSortBy] = useState<keyof ProductSyncResultTableModel>("sourceProductName");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const theme = useTheme();

  const syncResults = useMemo<ProductSyncResultTableModel[]>(() => {
    // the logic is taken from old UI
    const getSourceProductName = (syncResult: ProductSyncResult): string => {
      if (syncResult?.sourceProduct?.name) return syncResult.sourceProduct.name;

      if (syncResult?.targetProduct?.id) return syncResult.targetProduct.id;

      return "";
    };

    const getSku = (syncResult: ProductSyncResult): string => {
      if (syncResult?.sourceProduct?.sku) return syncResult.sourceProduct.sku;

      if (syncResult.targetProduct?.sku) return syncResult.targetProduct.sku;

      return "";
    };

    return (
      productDigest?.digest.results
        .map((x) => ({
          sourceProductId: x.sourceProduct?.id ?? "",
          sourceProductName: getSourceProductName(x),
          sourceProductSku: getSku(x),
          targetProductId: x.targetProduct?.id ?? "",
          targetProductName: x.targetProduct?.name ?? "",
          costUnitPrice: x.sourceProduct?.costUnitPrice ?? 0,
          retailUnitPrice: x.sourceProduct?.retailUnitPrice ?? 0,
          outcome: x.outcome,
          errorMessage: x.errorMessage,
        }))
        .sort((a, b) => compareModelsFn(a, b, sortBy, sortDirection)) ?? []
    );
  }, [productDigest, sortBy, sortDirection]);

  const columnOptions = useMemo<SortableColumn<ProductSyncResultTableModel>[]>(
    () => [
      { label: "Source Product", field: "sourceProductName" },
      { label: "SKU", field: "sourceProductSku" },
      { label: "Cost Unit Price", field: "costUnitPrice" },
      { label: "Retail Unit Price", field: "retailUnitPrice" },
      { label: "Target Product", field: "targetProductName" },
      { label: "Status", field: "outcome" },
    ],
    [],
  );

  const onSortRequest = (
    newSortField: keyof ProductSyncResultTableModel,
    newSortDirection: SortDirection,
  ) => {
    setSortBy(newSortField);
    setSortDirection(newSortDirection);
  };

  const syncErrorDetails = (syncResult: ProductSyncResultTableModel) => {
    if (syncResult.outcome !== SyncOutcome.Error && syncResult.outcome !== SyncOutcome.Warning)
      return undefined;

    return (
      <Typography
        variant="body2"
        fontWeight="medium"
        color={syncResult.outcome === SyncOutcome.Error ? theme.palette.error.main : "inherit"}
      >
        {syncResult.errorMessage}
      </Typography>
    );
  };

  const DataTableBody = (
    <TableBody>
      {syncResults.map((r, i) => (
        <TableRowCollapsible
          key={r.sourceProductId + r.targetProductId + String(i)}
          columnCount={columnOptions.length}
          collapsibleContent={syncErrorDetails(r)}
          oneRow
        >
          <TableBodyCell label={r.sourceProductName} />
          <TableBodyCell label={r.sourceProductSku} />
          <TableBodyCell label={r.costUnitPrice} />
          <TableBodyCell label={r.retailUnitPrice} />
          <TableBodyCell label={r.targetProductName} />
          <TableBodySyncOutcomeCell syncOutcome={r.outcome} />
        </TableRowCollapsible>
      ))}
    </TableBody>
  );

  return (
    <TableContainer>
      <Table testId="Product Sync Details">
        <TableHeadSortable
          columns={columnOptions}
          onSortRequest={onSortRequest}
          sortBy={sortBy}
          sortDirection={sortDirection}
        />
        {isProductDigestFetching ? (
          <TableBodySkeleton colsCount={columnOptions.length} rowsCount={3} />
        ) : (
          DataTableBody
        )}
      </Table>
    </TableContainer>
  );
};

export default ProductSyncDetailsTable;
